/**
 * Reports review queue — matches design handoff screen-report-queue.jsx.
 *
 * Header with period selector + "Generate now" button.
 * 4 workload tiles: Awaiting/In review, Changes requested, Sent, Avg time-to-send.
 * Filter chips: My queue, Changes requested, Sent, All.
 * Report rows with contract logo, status badge, hours, last edited, days waiting.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ReportListItem, ReportStatus } from '@beacon/core';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { ContractLogo as SharedContractLogo } from '@/components/beacon/contract-logo.js';
import { contractDisplay } from '@/lib/contract-display.js';
import { useReports } from '@/hooks/use-reports.js';
import { beaconClient } from '@/lib/api/client.js';
import { cn } from '@/lib/cn.js';

type FilterId = 'mine' | 'changes' | 'sent' | 'all';

/** Build recent period options from today backwards. */
function recentPeriods(count: number): Array<{ id: string; label: string; full: string }> {
  const out: Array<{ id: string; label: string; full: string }> = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const id = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-AU', { month: 'short', timeZone: 'Australia/Sydney' });
    const full = d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'Australia/Sydney' });
    out.push({ id, label, full });
  }
  return out;
}

export function ReportsQueue() {
  const periods = useMemo(() => recentPeriods(3), []);
  const [period, setPeriod] = useState(periods[0]!.id);
  const allReports = useReports(undefined, period);
  const rows = allReports.data ?? [];
  const [filter, setFilter] = useState<FilterId>('mine');
  const navigate = useNavigate();

  const generate = useMutation({
    mutationFn: async () => {
      const res = await beaconClient.post(`/api/reports/generate?period=${encodeURIComponent(period)}`);
      return res as { generated: number };
    },
    onSuccess: (data) => {
      toast.success(`Generated ${data.generated} reports`);
      allReports.refetch();
    },
    onError: () => toast.error('Failed to generate reports'),
  });

  const periodFull = periods.find((p) => p.id === period)?.full ?? period;

  // Workload stats
  const stats = useMemo(() => {
    let awaiting = 0;
    let inReview = 0;
    let changes = 0;
    let sent = 0;
    for (const r of rows) {
      if (r.status === 'draft' && r.rejectionReason) changes += 1;
      else if (r.status === 'draft') awaiting += 1;
      else if (r.status === 'review') inReview += 1;
      else if (r.status === 'sent') sent += 1;
    }
    return { awaiting, inReview, changes, sent, total: rows.length };
  }, [rows]);

  // Filtered rows
  const filtered = useMemo(() => {
    if (filter === 'mine')
      return rows.filter(
        (r) => r.status === 'draft' && !r.rejectionReason || r.status === 'review'
      );
    if (filter === 'changes')
      return rows.filter((r) => r.status === 'draft' && r.rejectionReason);
    if (filter === 'sent')
      return rows.filter((r) => r.status === 'sent');
    return rows;
  }, [rows, filter]);

  const stateBadge = (r: ReportListItem) => {
    if (r.status === 'draft' && r.rejectionReason)
      return <Badge tone="red" className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-danger-500" />Changes requested</Badge>;
    if (r.status === 'draft')
      return <Badge tone="amber" className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-warn-500" />Awaiting review</Badge>;
    if (r.status === 'review')
      return <Badge tone="violet" className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />In review</Badge>;
    if (r.status === 'approved')
      return <Badge tone="blue">Approved</Badge>;
    if (r.status === 'sent')
      return <Badge tone="green" className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-ok-500" />Sent</Badge>;
    return <Badge>{r.status}</Badge>;
  };

  const daysWaiting = (r: ReportListItem): number => {
    const gen = new Date(r.generatedAt);
    return Math.floor((Date.now() - gen.getTime()) / 86_400_000);
  };

  if (allReports.isLoading) return <ReportsQueueSkeleton />;

  return (
    <div className="h-full min-h-0 overflow-auto scrollbar-thin">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850">
        <div className="flex items-center gap-2 text-[12px] text-ink-300 mb-1">
          <span>Reports</span>
          <ChevronRight size={12} />
          <span className="text-ink-200">Review queue</span>
        </div>
        <div className="flex items-end gap-4">
          <div>
            <h1 className="text-[18px] font-semibold leading-tight">
              Reports waiting on you
            </h1>
            <div className="text-[12.5px] text-ink-300 mt-0.5">
              {periodFull} cycle · monthly cadence
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Period toggle */}
            <div className="bg-ink-800 border border-ink-600 rounded-md p-0.5 inline-flex">
              {periods.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={cn(
                    'h-7 px-2.5 rounded-[5px] text-[12px] font-medium transition-colors',
                    period === p.id
                      ? 'bg-ink-700 text-ink-100'
                      : 'text-ink-300 hover:text-ink-100'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button
              variant="primary"
              size="md"
              disabled={generate.isPending}
              onClick={() => generate.mutate()}
            >
              <FileText size={14} />
              {generate.isPending ? 'Generating…' : 'Generate now'}
            </Button>
          </div>
        </div>

      </div>

      {/* Filter chips — same pattern as inbox */}
      <div className="px-6 py-2.5 flex items-center gap-2 bg-ink-900 overflow-x-auto">
        {([
          ['mine', 'My queue', stats.awaiting + stats.inReview],
          ['changes', 'Changes requested', stats.changes],
          ['sent', 'Sent', stats.sent],
          ['all', 'All open', rows.length],
        ] as const).map(([id, label, count]) => (
          <button
            key={id}
            onClick={() => setFilter(id as FilterId)}
            aria-pressed={filter === id}
            className={cn(
              'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12.5px] font-medium border transition-colors focus-ring whitespace-nowrap',
              filter === id
                ? 'bg-ink-100 text-ink-900 border-ink-100'
                : 'text-ink-200 border-ink-600 hover:bg-ink-800'
            )}
          >
            {label}
            <span
              className={cn(
                'mono text-[10.5px] px-1 rounded',
                filter === id
                  ? 'bg-ink-800 text-ink-200'
                  : 'bg-ink-700 text-ink-300'
              )}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Column header */}
      <div className="px-6 h-8 border-b border-ink-600 bg-ink-850 text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold flex items-center gap-3">
        <div className="w-[200px]">Contract</div>
        <div className="w-[80px] mono">Month</div>
        <div className="w-[110px]">State</div>
        <div className="flex-1">Hours</div>
        <div className="w-[120px]">Generated</div>
        <div className="w-[60px] text-right">Days</div>
        <div className="w-[140px] text-right">Action</div>
      </div>

      {/* Rows */}
      <div>
        {filtered.map((r) => {
          const days = daysWaiting(r);
          return (
            <div
              key={r.id}
              className="px-6 h-[64px] flex items-center gap-3 border-b border-ink-600/60 hover:bg-ink-800/40 group cursor-pointer"
              onClick={() => navigate(`/reports/${encodeURIComponent(r.id)}`)}
            >
              <div className="w-[200px] flex items-center gap-2 min-w-0">
                <SharedContractLogo contract={contractDisplay({ id: r.contractId, name: r.contractName } as import('@beacon/core').Contract)} size={28} />
                <div className="leading-tight min-w-0">
                  <div className="text-[13px] text-ink-100 truncate">
                    {r.contractName}
                  </div>
                  <div className="text-[11px] text-ink-400 mono truncate">
                    {r.contractType?.toUpperCase() ?? ''}
                  </div>
                </div>
              </div>
              <div className="w-[80px] mono text-[12px] text-ink-200">
                {monthLabel(r.periodStartDate)}
              </div>
              <div className="w-[110px]">{stateBadge(r)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] mono text-ink-100">
                  {r.sentToEmails.length > 0
                    ? `Sent to ${r.sentToEmails.length} recipient${r.sentToEmails.length > 1 ? 's' : ''}`
                    : '—'}
                </div>
              </div>
              <div className="w-[120px] text-[12px]">
                <div className="text-ink-200 mono">{shortDate(r.generatedAt)}</div>
              </div>
              <div className="w-[60px] text-right">
                <span
                  className={cn(
                    'mono text-[14px] font-semibold',
                    days >= 2
                      ? 'text-danger-400'
                      : days === 1
                        ? 'text-warn-400'
                        : 'text-ink-300'
                  )}
                >
                  {days}d
                </span>
              </div>
              <div className="w-[140px] flex justify-end" onClick={(e) => e.stopPropagation()}>
                {r.status === 'sent' ? (
                  <Link to={`/reports/${encodeURIComponent(r.id)}`}>
                    <Button variant="ghost" size="sm">
                      <ExternalLink size={12} />View
                    </Button>
                  </Link>
                ) : r.status === 'draft' && r.rejectionReason ? (
                  <Link to={`/reports/${encodeURIComponent(r.id)}`}>
                    <Button variant="secondary" size="sm">Awaiting team</Button>
                  </Link>
                ) : (
                  <Link to={`/reports/${encodeURIComponent(r.id)}`}>
                    <Button variant="primary" size="sm">
                      Open review
                      <ChevronRight size={12} />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="px-6 py-16 flex items-center justify-center">
          <div className="text-center">
            <span className="w-12 h-12 rounded-full bg-ok-500/15 text-ok-400 inline-flex items-center justify-center mb-3">
              <Check size={20} />
            </span>
            <div className="text-[14px] text-ink-100">All clear in this view</div>
            <div className="text-[12px] text-ink-300 mt-1">
              Try the "All" filter to see what's been sent.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Skeleton ── */
function ReportsQueueSkeleton() {
  return (
    <div className="h-full" aria-busy="true">
      <div className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850 space-y-3">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-3 w-40" />
      </div>
      <div className="px-6 py-2.5 flex items-center gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-24 rounded-md" />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="px-6 h-[64px] flex items-center gap-3 border-b border-ink-600/60">
          <Skeleton className="w-7 h-7 rounded-[5px]" />
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-4 flex-1 max-w-[100px]" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-8" />
          <Skeleton className="h-7 w-24 ml-auto" />
        </div>
      ))}
    </div>
  );
}

/* ── Helpers ── */
function monthLabel(ymd: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(ymd);
  if (!m) return ymd;
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${months[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
